<template>
  <div class="table-wrapper">
    <set-upi-prefix v-if="upiPrefix.status === 'NOT_ADDED'" @refreshList="populateData" />
    <pending-upi-prefix v-else-if="upiPrefix.status === 'PENDING'" :prefixData="upiPrefix" />

    <div class="upi-list" v-else>
      <upi-prefix-list v-if="getterPopupName === 'UpiPrefixList'" :prefixData="upiPrefix" />
      <upi-create v-if="getterPopupName === 'UpiCreate'" :prefixData="upiPrefix"
                  @refreshList="updateFilters" />
      <upi-export-filter v-else-if="['UpiExport', 'UpiFilter'].includes(getterPopupName)"
                  @updateFilters="updateFilters" />

      <div class="table-title">
        <div class="subject">
          <p>UPIs</p>
        </div>

        <div class="filters">
          <div class="options">
            <button @click="openPopup('UpiPrefixList')">
              <span>{{isDesktop() ? 'UPI Prefixes' : 'Prefixes'}}</span>
              <i class="fa fa-address-book" />
            </button>
            <button @click="openPopup('UpiCreate')" v-if="manageAccess.upi"
                    vt-data="create">
              <span>{{isDesktop() ? 'Create UPI' : 'Create'}}</span>
              <i class="fa fa-plus" />
            </button>
            <button @click="openPopup('UpiExport')">
              Export
              <i class="fa fa-share" />
            </button>
            <button @click="openPopup('UpiFilter')"
                    :class="{'filters-applied': filtersApplied}"
                    class="dark-btn">
              Filter
              <i class="fa fa-filter" />
            </button>
          </div>

          <upi-search placeholder="Search ID / UPI ID" :suffix="true"
                  @populateList="clearSearch"
                  @setFunctions="setSearchFunctions"
                  :triggerSearch="triggerSearch" :searchTerm="searchTerm" />
        </div>
      </div>

      <div class="table-head" v-if="isDesktop()">
        <p>ID</p>
        <p class="d-xl">UPI</p>
        <p class="d-l">Created Time</p>
        <p>Name</p>
        <p>PAN</p>
        <p>Category</p>
        <p>Status</p>
      </div>

      <div class="table-body">
        <div class="table-row cursor"
          v-for="(upi, index) in list" :key="index"
          @click="openTransaction(upi.id)"
        >
          <p>
            <b v-if="!isDesktop()">ID:</b>
            <span>{{upi.id}}</span>
          </p>
          <p class="d-xl">
            <span>{{upi.upi_id}}</span>
          </p>
          <p class="d-l">
            {{upi.created_at}}
          </p>
          <p>
            <b v-if="!isDesktop()">Name:</b>
            <marquee class="pre-space" :scrollamount="4" v-if="upi.name.length >= 20">
              {{upi.name}}
            </marquee>
            <span class="pre-space" v-else>{{upi.name}}</span>
          </p>
          <p>
            <b v-if="!isDesktop()">PAN:</b>
            {{upi.pan}}
          </p>
          <p>
            <b v-if="!isDesktop()">Category:</b>
            {{upi.category}}
          </p>
          <p>
            {{upi.status}}
          </p>
        </div>
      </div>

      <table-loader v-if="!paginate.data_added || !paginate.stop" />

      <div class="table-empty" v-else-if="list.length === 0">
        <p v-if="searchTerm">Unable to find UPIs with <span class="highlight">{{searchTerm}}</span>.<br>Please search with the complete ID / UPI ID</p>
        <p v-else>{{emptyRecords}}</p>
      </div>

      <promotion-banner
        :banner-img="BannerImg"
      />
    </div>
  </div>
</template>

<script>
import UpiCreate from '@/components/UPIs/CreateUpi'
import UpiExportFilter from '@/components/UPIs/UpiExportFilter'
import SetUpiPrefix from '@/components/UPIs/SetUpiPrefix'
import PendingUpiPrefix from '@/components/UPIs/PendingUpiPrefix'
import UpiPrefixList from '@/components/UPIs/UpiPrefixList'
import UpiSearch from '@/components/global/SearchRecords'
import PromotionBanner from '@/components/global/PromotionBanner'

import BannerImg from '@/assets/images/GCP_3.gif'

import ApiServices from '@/services/api/upi'
import listMixins from '@/mixins/listMixins'
import { mapMutations } from 'vuex'

export default {
  name: 'UPIList',
  components: { SetUpiPrefix, UpiCreate, UpiExportFilter, PendingUpiPrefix, UpiPrefixList, UpiSearch, PromotionBanner },
  mixins: [listMixins],
  data () {
    return {
      BannerImg,
      upiPrefix: false,
      manageAccess: {}
    }
  },
  created () {
    this.startProcessing()
  },
  methods: {
    ...mapMutations(['SET_UNPRIVILEGE_PAGE', 'SET_UPI_CONNECTED_BANKING']),
    populateParams () {
      this.api['search'] = ApiServices.search
      this.paginate['type'] = 'UPI'
      this.keys['list'] = 'upis'
      this.params['filtersLen'] = 0
      this.func['searchPrior'] = () => {
        if ((/^[a-zA-Z]+/).test(this.searchTerm)) {
          this.searchTerm += this.searchTerm.startsWith('hypto.') ? '@icici' : '@yesbank'
        }
      }
      this.setHighlightSelectors([1, 2])
      this.paginate['token_type'] = 'next_page_token'
      this.paginate['no_search_export'] = true
    },
    fetchRecords (start) {
      return new Promise(resolve => {
        let params = {...this.filterOptions}
        if (start) {
          params['next_page_token'] = this.paginate['next_page_token']
        }

        ApiServices.upiList(params).then((response) => {
          this.SET_STATEMENT_FILTER(this.filterOptions)

          let data = response.data
          if (data) {
            this.setAccess(response.privileges)

            if (start) {
              this.list.push(...data.upis)
              this.checkPaginateV2(data.next_page_token)
            } else {
              this.populateMeta(response)

              if (this.upiPrefix.status === 'APPROVED') {
                this.list = data.upis
                this.setPaginate(data)
              } else if (!this.manageAccess.upi) {
                this.upiPrefix.status === 'NOT_ADDED' ? this.SET_UNPRIVILEGE_PAGE('Configure UPI') : this.SET_UNPRIVILEGE_PAGE('Pending Approval')
              }
            }
          }

          resolve()
        })
      })
    },
    openTransaction (id) {
      this.$router.push({name: 'UPITransactions', params: {id}})
    },
    populateMeta ({data, privileges}, fromSearch) {
      this.upiPrefix = data.upi_prefix
      this.SET_UPI_CONNECTED_BANKING(data.connected_banking_enabled)

      if (fromSearch) {
        this.setAccess(privileges)
        const status = this.upiPrefix.status
        if (status !== 'APPROVED' && !privileges.manage_upis) {
          status === 'NOT_ADDED' ? this.SET_UNPRIVILEGE_PAGE('Configure UPI') : this.SET_UNPRIVILEGE_PAGE('Pending Approval')
        }
      }
    },
    setAccess (privileges) {
      this.manageAccess = {
        upi: privileges.manage_upis
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '@/assets/scss/table.scss';

  .table-row {
    p {
      word-break: break-word;
    }
  }

  .mobile, .tablet {
    .table-row {
      grid-template-areas: "upi upi"
                            "id time"
                            "name pan"
                            "category status";

      p {
        white-space: unset !important;

        &:nth-child(1) {
          grid-area: id;
          color: $grey-text;
        }
        &:nth-child(2) {
          grid-area: upi;
          color: $primary;
        }
        &:nth-child(3) {
          grid-area: time;
          text-align: right;
          color: $grey-text;
        }
        &:nth-child(4) {
          grid-area: name;
        }
        &:nth-child(5) {
          grid-area: pan;
          text-align: right;
        }
        &:nth-child(6) {
          grid-area: category;
        }
        &:nth-child(7) {
          grid-area: status;
          text-align: right;
          color: $grey-text;
        }
      }
    }
  }
</style>
